import React, { useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  DndContext,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AppIcon from '../../HomeLauncher/components/AppIcon';
import { buildScreenSlots } from './panelModel';

/** Keep tiles usable at max 8×12 — grid scrolls instead of crushing cells. */
const MIN_CELL_PX = 88;

function emptySlotId(index) {
  return `empty-${index}`;
}

function parseSlotIndex(id, slots) {
  const raw = String(id);
  if (raw.startsWith('empty-')) {
    const n = Number(raw.slice('empty-'.length));
    return Number.isInteger(n) ? n : -1;
  }
  return slots.findIndex((btn) => btn?.id === raw);
}

function SortableButton({ btn, editMode, onButtonEdit, onButtonClick, suppressClickRef }) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: btn.id,
    disabled: !editMode,
  });

  return (
    <Box
      ref={setNodeRef}
      {...(editMode ? { ...attributes, ...listeners } : {})}
      onClick={() => {
        if (suppressClickRef?.current) return;
        if (editMode) onButtonEdit?.(btn);
        else onButtonClick?.(btn);
      }}
      sx={{
        position: 'relative',
        minHeight: MIN_CELL_PX,
        minWidth: MIN_CELL_PX,
        height: '100%',
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : 'auto',
        opacity: isDragging ? 0.85 : 1,
        cursor: editMode ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
        touchAction: 'none',
        '& img': { maxHeight: 36, maxWidth: 36 },
        '& svg': { width: 28, height: 28 },
      }}
    >
      <AppIcon
        icon={btn}
        editMode={false}
        dark
        iconSize="small"
        isProtected={false}
        labelInside
        hideDelete
        jiggle={false}
        onClick={() => {}}
      />
    </Box>
  );
}

function SortableEmptySlot({ id, editMode, onClick }) {
  const { setNodeRef, isOver, transform, transition } = useSortable({
    id,
    disabled: !editMode,
  });

  return (
    <Box
      ref={setNodeRef}
      onClick={() => editMode && onClick?.()}
      sx={{
        borderRadius: 2,
        border: '1px dashed',
        borderColor: isOver ? 'primary.main' : 'rgba(255,255,255,0.25)',
        bgcolor: isOver ? 'rgba(59,130,246,0.18)' : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: editMode ? 'pointer' : 'default',
        color: 'rgba(255,255,255,0.5)',
        minHeight: MIN_CELL_PX,
        minWidth: MIN_CELL_PX,
        height: '100%',
        transform: CSS.Transform.toString(transform),
        transition,
        touchAction: 'none',
        '&:hover': editMode
          ? { borderColor: 'rgba(255,255,255,0.55)', bgcolor: 'rgba(255,255,255,0.04)' }
          : undefined,
      }}
    >
      {editMode ? <AddIcon fontSize="small" /> : null}
    </Box>
  );
}

/**
 * Screen grid canvas for Touch Panel Designer.
 * Fills available space; cells keep a usable min size and the grid scrolls when needed.
 */
export default function ScreenCanvas({
  screen,
  backgroundColor,
  editMode,
  onEmptySlotClick,
  onButtonClick,
  onButtonEdit,
  onButtonMove,
}) {
  const columns = Math.max(1, Number(screen?.columns) || 1);
  const rows = Math.max(1, Number(screen?.rows) || 1);
  const slots = useMemo(() => buildScreenSlots(screen), [screen]);
  const suppressClickRef = useRef(false);

  const sortableIds = useMemo(
    () => slots.map((btn, idx) => (btn ? btn.id : emptySlotId(idx))),
    [slots]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  const handleDragStart = () => {
    suppressClickRef.current = true;
  };

  const handleDragEnd = (event) => {
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);

    if (!editMode) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = parseSlotIndex(active.id, slots);
    const to = parseSlotIndex(over.id, slots);
    if (from < 0 || to < 0 || from === to) return;
    onButtonMove?.(from, to);
  };

  const handleDragCancel = () => {
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        p: 1.5,
        borderRadius: 2,
        bgcolor: backgroundColor || '#0f172a',
        boxShadow: 3,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${columns}, minmax(${MIN_CELL_PX}px, 1fr))`,
                gridTemplateRows: `repeat(${rows}, minmax(${MIN_CELL_PX}px, 1fr))`,
                gap: 1,
                width: '100%',
                minHeight: '100%',
                boxSizing: 'border-box',
              }}
            >
              {slots.map((btn, idx) =>
                btn ? (
                  <SortableButton
                    key={btn.id}
                    btn={btn}
                    editMode={editMode}
                    onButtonEdit={onButtonEdit}
                    onButtonClick={onButtonClick}
                    suppressClickRef={suppressClickRef}
                  />
                ) : (
                  <SortableEmptySlot
                    key={emptySlotId(idx)}
                    id={emptySlotId(idx)}
                    editMode={editMode}
                    onClick={() => onEmptySlotClick?.(idx)}
                  />
                )
              )}
            </Box>
          </SortableContext>
        </DndContext>
      </Box>
      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 0.75, color: 'rgba(255,255,255,0.55)', flexShrink: 0 }}
      >
        {editMode ? 'Drag to move · tap to edit · scroll when the grid is large' : 'Preview'}
      </Typography>
    </Box>
  );
}
